import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';

const ENTITY_TYPES = [
  { label: 'Temple', value: 'TEMPLE' },
  { label: 'Teertha', value: 'TEERTHA' },
  { label: 'Deity', value: 'DEITY' },
  { label: 'Saint', value: 'SAINT' },
  { label: 'Project', value: 'PROJECT' },
  { label: 'Live Darshan', value: 'LIVE_DARSHAN' },
];

const EntityAnalyticsPage = () => {
  const [entityType, setEntityType] = useState('TEMPLE');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideCount, setOverrideCount] = useState('');
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setResults([]);
    setQuery('');
    setSelectedEntity(null);
    setAnalytics(null);
  }, [entityType]);

  const runSearch = useCallback((q) => {
    if (!q) { setResults([]); return; }
    setSearching(true);
    _axios.get(`${ServerPath}/entity-analytics/search`, { params: { entity_type: entityType, q } })
      .then(res => {
        if (res.data?.success) setResults(res.data.data || []);
      })
      .catch(err => {
        console.error('entity search error:', err);
        toast.error('Failed to search entities');
      })
      .finally(() => setSearching(false));
  }, [entityType]);

  const handleQueryChange = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const fetchAnalytics = (entity) => {
    _axios.get(`${ServerPath}/entity-analytics/${entity.entity_type}/${entity.entity_id}`)
      .then(res => {
        if (res.data?.success) {
          const data = res.data.data;
          setAnalytics(data);
          setOverrideEnabled(data.like_override_enabled);
          setOverrideCount(data.like_override_count || '');
        }
      })
      .catch(err => {
        console.error('fetch analytics error:', err);
        toast.error('Failed to load analytics for this entity');
      });
  };

  const handleSelectEntity = (entity) => {
    setSelectedEntity(entity);
    setResults([]);
    setQuery(entity.name);
    fetchAnalytics(entity);
  };

  // Live concurrent-viewer count only applies to a LIVE_DARSHAN entity that's
  // actually live_now right now -- polled separately from fetchAnalytics (not
  // via that same function on an interval) so it doesn't stomp on in-progress
  // edits to the override form below while the admin is mid-edit. Silent on
  // failure -- a missed 15s tick isn't worth toast-spamming the admin over.
  useEffect(() => {
    if (!selectedEntity || selectedEntity.entity_type !== 'LIVE_DARSHAN') return;
    const interval = setInterval(() => {
      _axios.get(`${ServerPath}/entity-analytics/${selectedEntity.entity_type}/${selectedEntity.entity_id}`)
        .then(res => {
          if (res.data?.success) {
            const data = res.data.data;
            setAnalytics(prev => prev ? { ...prev, current_viewers: data.current_viewers, stream_status: data.stream_status } : prev);
          }
        })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedEntity]);

  const handleSave = async () => {
    if (!selectedEntity) return;
    if (overrideEnabled && (overrideCount === '' || isNaN(overrideCount) || Number(overrideCount) < 0 || !Number.isInteger(Number(overrideCount)))) {
      toast.error('Enter a valid non-negative whole number for the manual count');
      return;
    }
    setSaving(true);
    try {
      const res = await _axios.post(`${ServerPath}/entity-analytics/override`, {
        entity_type: selectedEntity.entity_type,
        entity_id: selectedEntity.entity_id,
        like_override_enabled: overrideEnabled,
        like_override_count: overrideEnabled ? Number(overrideCount) : 0,
      });
      if (res.data?.success) {
        toast.success('Changes saved');
        fetchAnalytics(selectedEntity);
      } else {
        toast.error(res.data?.message || 'Failed to save');
      }
    } catch (err) {
      console.error('save override error:', err);
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!analytics) return;
    setOverrideEnabled(analytics.like_override_enabled);
    setOverrideCount(analytics.like_override_count || '');
  };

  const finalDisplayedLikes = overrideEnabled
    ? (analytics?.genuine_likes || 0) + (Number(overrideCount) || 0)
    : (analytics?.genuine_likes || 0);

  return (
    <Container fluid>
      <h3 className="mb-3">Analytics & Display Counts</h3>
      <p className="text-muted mb-4">
        View analytics and configure display counts for Temples, Teerthas, Deities, Saints, Projects and Live Darshan.
      </p>

      <Card className="mb-4">
        <CardHeader><strong>1. Select Entity</strong></CardHeader>
        <CardBody>
          <Row>
            <Col md="3">
              <Form.Label>Entity Type</Form.Label>
              <Form.Select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
                {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Form.Select>
            </Col>
            <Col md="6" className="position-relative">
              <Form.Label>Search Entity</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name..."
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
              />
              {results.length > 0 && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm w-100"
                  style={{ zIndex: 1000, maxHeight: '260px', overflowY: 'auto' }}
                >
                  {results.map(r => (
                    <div
                      key={r.entity_id}
                      className="d-flex align-items-center gap-2 p-2 border-bottom"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectEntity(r)}
                    >
                      {r.thumbnail && (
                        <img src={r.thumbnail} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                      )}
                      <span>{r.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {searching && <div className="text-muted small mt-1">Searching...</div>}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {selectedEntity && analytics && (
        <Card>
          <CardHeader className="d-flex align-items-center gap-2">
            {selectedEntity.thumbnail && (
              <img src={selectedEntity.thumbnail} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
            )}
            <div>
              <strong>{selectedEntity.name}</strong>
              <div className="text-muted small">
                {ENTITY_TYPES.find(t => t.value === selectedEntity.entity_type)?.label}
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <h6 className="text-muted">Analytics Summary (Read Only)</h6>
                <div className="d-flex gap-4 mb-3">
                  <div>
                    <div className="text-muted small">Displayed Likes</div>
                    <div className="fs-4 fw-bold">{analytics.displayed_likes}</div>
                  </div>
                  <div>
                    <div className="text-muted small">Total Views</div>
                    <div className="fs-4 fw-bold">{analytics.view_count}</div>
                  </div>
                  {analytics.entity_type === 'LIVE_DARSHAN' && analytics.stream_status === 'live_now' && (
                    <div>
                      <div className="text-muted small">Current Viewers</div>
                      <div className="fs-4 fw-bold text-success">{analytics.current_viewers ?? 0}</div>
                    </div>
                  )}
                </div>
              </Col>
              <Col md="6">
                <h6 className="text-muted">Manual Like Count Override</h6>
                <Form.Check
                  type="checkbox"
                  id="override-enabled"
                  label="Enable Manual Like Count Override"
                  checked={overrideEnabled}
                  onChange={(e) => setOverrideEnabled(e.target.checked)}
                  className="mb-2"
                />
                {overrideEnabled && (
                  <Form.Group className="mb-2" style={{ maxWidth: 200 }}>
                    <Form.Label className="small">Manual Count to Add</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="1"
                      value={overrideCount}
                      onChange={(e) => setOverrideCount(e.target.value.split('.')[0].replace(/[^0-9]/g, ''))}
                    />
                  </Form.Group>
                )}
                <div className="bg-light rounded p-2 mb-3">
                  <div className="text-muted small">Final Displayed Like Count (for users)</div>
                  <div className="fs-5 fw-bold text-warning">{finalDisplayedLikes}</div>
                  <div className="text-muted small">
                    (Actual Likes {analytics.genuine_likes} + Manual Count {overrideEnabled ? (Number(overrideCount) || 0) : 0})
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                  <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}
    </Container>
  );
};

export default EntityAnalyticsPage;
